import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { useMutation } from '@tanstack/react-query';
import { FileText, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getFile } from '@/services/file';

type TAttachmentBadgeProps = {
  attachment: TAttachment;
};

export type TAttachment = {
  filePath: string;
  fileName: string;
};

export const AttachmentBadge = (props: TAttachmentBadgeProps) => {
  const { t } = useTranslation();
  const fileMutation = useMutation({
    mutationFn: getFile,
    onSuccess: () => {},
  });

  const isDisabled = fileMutation.isPending;

  const handleClick = async () => {
    if (isDisabled) return;
    const response = await fileMutation.mutateAsync({
      filePath: props.attachment.filePath,
      view: 'inline',
    });

    const blob = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = props.attachment.fileName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success(t('File downloaded successfully'));
  };

  const shortName =
    props.attachment.fileName.length > 30
      ? `${props.attachment.fileName.slice(0, 20)}...`
      : props.attachment.fileName;

  return (
    <Badge
      variant='outline'
      title={props.attachment.fileName}
      className={`bg-white p-2 ${isDisabled ? 'opacity-50' : 'hover:cursor-pointer hover:bg-accent hover:text-accent-foreground'}`}
      onClick={handleClick}
    >
      {isDisabled ? (
        <Loader2 className='mr-2 animate-spin' size={12} />
      ) : (
        <FileText className='mr-2' size={12} />
      )}
      {shortName}
    </Badge>
  );
};
